import { FinancialCalculator } from '@classes/financial';

export const ACK_ACTIVATING_DESTINATIONS = [
  'SEND TO STACK',
  'ORDERED FRAME',
  'ORDERED',
  'ORDERED ON APPROVAL',
  'ORDERED LENSES',
  'SAVE/HOLD',
  'HOLD',
  'CALL FOR RX',
  'CALL / RX',
  'TO REPAIR',
  'REPAIR',
  'SENT FOR REPAIR',
  'ADD TO CART',
  'SHIPPED',
  'RECEIVED FRAME',
  'RECEIVED LENSES',
  'CALLED FOR RX',
  'RX RECEIVED',
  'CONVERT TO ORDER',
];

export const ACK_STAFF_METHODS = [
  'Verbal Approval by Phone',
  'Long-standing Customer Authorization',
  'Customer Unavailable',
  'Manager-approved Exception',
  'Other',
];

export function isAckExemptStatus(status?: string): boolean {
  const value = String(status || '').toUpperCase();
  return (
    value === 'QUOTE' ||
    value === 'INQUIRY' ||
    value === 'VOID' ||
    value === 'CANCELED' ||
    value === 'RETURNED' ||
    value === 'RETURNING' ||
    value === 'RETURN'
  );
}

export function isFrameOnlyOrPol(order: any): boolean {
  return !!order?.frameOnly || FinancialCalculator.isPolSelected(order);
}

export function acknowledgmentChargeRows(
  order: any
): { label: string; value: number }[] {
  const calculated = FinancialCalculator.calculate(order || {});
  const storedItems = order?.lineItems || [];
  const hasStoredCharges =
    storedItems.some((item) => FinancialCalculator.money(item?.amount) > 0) ||
    FinancialCalculator.money(order?.financials?.orderTotal) > 0;
  const items = hasStoredCharges && storedItems.length ? storedItems : calculated.lineItems;
  const financials = hasStoredCharges && order?.financials
    ? { ...calculated.financials, ...order.financials }
    : calculated.financials;
  const amountFor = (...categories: string[]) =>
    FinancialCalculator.money(
      items
        .filter((item) => categories.includes(String(item.category || '').toUpperCase()))
        .reduce((sum, item) => sum + FinancialCalculator.money(item.amount), 0)
    );
  return [
    { label: 'FRAME', value: amountFor('FRAME') },
    { label: 'LENSES', value: amountFor('LENSES', 'TINT', 'COATINGS') },
    { label: 'MISC', value: amountFor('MISC', 'CUSTOM') },
    { label: 'SUBTOTAL', value: FinancialCalculator.money(financials.subtotal) },
    { label: 'POSTAGE', value: FinancialCalculator.money(financials.postage) },
    { label: 'TAX', value: FinancialCalculator.money(financials.tax) },
    { label: 'ORDER TOTAL', value: FinancialCalculator.money(financials.orderTotal) },
    { label: 'PAID', value: FinancialCalculator.money(financials.paid) },
    { label: 'BALANCE DUE', value: FinancialCalculator.money(financials.balanceDue) },
  ];
}

export function needsRenewedAcknowledgment(order: any): boolean {
  const ack = order?.acknowledgment;
  if (!order?.acknowledgedAt || !ack) {
    return false;
  }
  const signedTotal = FinancialCalculator.money(ack.charges?.orderTotal);
  if (!(signedTotal > 0) && ack.charges?.orderTotal == null) {
    return false;
  }
  const currentTotal = FinancialCalculator.money(order?.financials?.orderTotal);
  return Math.abs(currentTotal - signedTotal) > 0.009;
}

export function isOrderAcknowledged(order: any): boolean {
  if (!order?.acknowledgedAt) {
    return false;
  }
  return !needsRenewedAcknowledgment(order);
}

export function shouldPromptAcknowledgment(
  order: any,
  destination: string
): boolean {
  if (isAckExemptStatus(order?.status) || destination === 'SAVE/QUOTE') {
    return false;
  }
  if (isOrderAcknowledged(order)) {
    return false;
  }
  if (destination === 'CUSTOMER ACKNOWLEDGEMENT' || destination === 'SEND ACKNOWLEDGEMENT') {
    return true;
  }
  if (destination === 'SAVE' && isFrameOnlyOrPol(order)) {
    return true;
  }
  return ACK_ACTIVATING_DESTINATIONS.includes(destination);
}

export function acknowledgmentNotice(order: any, patient?: any): string {
  const ack = order?.acknowledgment || {};
  if (needsRenewedAcknowledgment(order)) {
    return 'Price or job details changed — renewed acknowledgement required';
  }
  if (!order?.acknowledgedAt) {
    return '';
  }
  const name =
    String(ack.customerName || '').trim() ||
    [patient?.firstName, patient?.lastName].filter(Boolean).join(' ').trim();
  const when = order.acknowledgedAt
    ? new Date(order.acknowledgedAt)
    : ack.signedAt
    ? new Date(ack.signedAt)
    : null;
  const stamp =
    when && !isNaN(when.getTime())
      ? when.toLocaleString([], {
          month: 'numeric',
          day: 'numeric',
          year: '2-digit',
          hour: 'numeric',
          minute: '2-digit',
        })
      : '';
  if (ack.mode === 'STAFF_OVERRIDE') {
    const label =
      ack.authorizationMethod === 'Verbal Approval by Phone'
        ? 'Verbal Authorization Recorded'
        : 'Customer Signature Waived by Staff';
    return [label, name, stamp].filter(Boolean).join(' — ');
  }
  return ['Acknowledged', name, stamp].filter(Boolean).join(' — ');
}
